// The detected user locale, shared by the number, currency and date formatters.
// Lives here (a leaf module with no imports) so `dates.ts` and `utils.ts` can both
// depend on it without importing each other — they used to, forming a cycle.
//
// International English: "1,234.50" and day-first dates ("31/12/2025").
export const LOCALE_EN_GB = "en-GB";
export const DEFAULT_LOCALE = LOCALE_EN_GB;
// Only a fallback: in practice every browser populates navigator.languages.
export const USERS_LOCALE = navigator.languages?.[0] || DEFAULT_LOCALE;

export interface Locale {
  /** Full locale token as received, e.g. "es-ES" or "es". */
  code: string;
  /** Region/country part when present (the "ES" in "es-ES"), else undefined. */
  region?: string;
}

/**
 * Splits a browser `languages` string (e.g. "es-ES;es;en-US;en") into ordered,
 * de-noised locale tokens, extracting the region when a token carries one.
 */
export const parseLocales = (raw: string): Locale[] =>
  raw
    .split(";")
    .map((token) => token.trim())
    .filter(Boolean)
    .map((code) => {
      const [, region] = code.split("-");
      return { code, region };
    });

const languageDisplayNames = new Intl.DisplayNames(["en"], {
  type: "language",
});

/** `Intl.DisplayNames.of` throws on structurally invalid codes — swallow it. */
const safeLanguageName = (value: string): string | undefined => {
  try {
    return languageDisplayNames.of(value) ?? undefined;
  } catch {
    return undefined;
  }
};

/**
 * Human-readable label for a locale token, e.g. "de-DE" → "German (Germany)",
 * "de" → "German".
 *
 * The language name is resolved from the engine's `Intl.DisplayNames` (the raw
 * locale code is always shown on the chip itself, so engine-to-engine drift is
 * low-stakes here). The country name, when present, is supplied by the caller —
 * typically from the app's curated countries list — so it stays consistent with
 * the rest of the UI. Falls back to the raw code when the language can't be
 * resolved, and omits the parenthetical when no country name is available.
 */
export const describeLocale = (
  { code }: Locale,
  regionName?: string
): string => {
  const [language = code] = code.split("-");
  const languageName = safeLanguageName(language) ?? code;
  return regionName ? `${languageName} (${regionName})` : languageName;
};
